"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Bell, X, MessageCircle, Package, ShoppingCart, CheckCircle2 } from "lucide-react";
import {
  getUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  deleteAllNotifications,
  subscribeToNotifications,
} from "../../lib/api/notifications";
import { Notification } from "../../data/types";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";

interface NotificationDropdownProps {
  userId: string;
  onClose: () => void;
}

export function NotificationDropdown({ userId, onClose }: NotificationDropdownProps) {
  const router = useRouter();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // 알림 구독
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = subscribeToNotifications(
      userId,
      (newNotifications) => {
        // 최신 10개만 표시
        setNotifications(newNotifications.slice(0, 10));
        setIsLoading(false);
      },
      (error) => {
        console.error("알림 구독 오류:", error);
        setIsLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [userId]);

  const handleNotificationClick = async (notification: Notification) => {
    // 알림 읽음 처리
    if (!notification.isRead) {
      try {
        await markNotificationAsRead(notification.id, userId); // userId 추가!
      } catch (error) {
        console.error("알림 읽음 처리 실패:", error);
      }
    }

    // 링크로 이동
    if (notification.link) {
      router.push(notification.link);
      onClose();
    }
  };

  // 개별 알림 삭제
  const handleDeleteNotification = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation(); // 클릭 이벤트 전파 방지
    
    try {
      const result = await deleteNotification(notificationId, userId);
      if (!result.success) {
        console.error("알림 삭제 실패:", result.error);
      }
    } catch (error) {
      console.error("알림 삭제 오류:", error);
    }
  };

  // 모두 읽음 처리
  const handleMarkAllAsRead = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    try {
      const result = await markAllNotificationsAsRead(userId);
      if (!result.success) {
        console.error("모두 읽음 처리 실패:", result.error);
      }
    } catch (error) {
      console.error("모두 읽음 처리 오류:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // 모두 삭제
  const handleDeleteAll = async () => {
    if (isProcessing) return;
    
    if (!confirm("모든 알림을 삭제하시겠습니까?")) {
      return;
    }
    
    setIsProcessing(true);
    try {
      const result = await deleteAllNotifications(userId);
      if (!result.success) {
        console.error("모두 삭제 실패:", result.error);
      }
    } catch (error) {
      console.error("모두 삭제 오류:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "new_message":
        return <MessageCircle className="w-5 h-5 text-[#7A56E8]" />;
      case "transaction_update":
      case "product_sold":
        return <ShoppingCart className="w-5 h-5 text-green-600" />;
      case "product_inquiry":
        return <Package className="w-5 h-5 text-blue-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return "";
    try {
      const date = timestamp.toDate
        ? timestamp.toDate()
        : new Date(timestamp.seconds * 1000);
      return formatDistanceToNow(date, { addSuffix: true, locale: ko });
    } catch {
      return "";
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-full mt-2 w-[360px] max-h-[500px] bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden"
      style={{
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
      }}
    >
      {/* 헤더 */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">알림</h3>
          {notifications.length > 0 && (
            <span className="text-xs text-gray-500">
              ({notifications.filter(n => !n.isRead).length}개 안읽음)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {notifications.length > 0 && (
            <>
              <button
                onClick={handleMarkAllAsRead}
                disabled={isProcessing || notifications.every(n => n.isRead)}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                모두 읽음
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={handleDeleteAll}
                disabled={isProcessing}
                className="text-xs text-red-600 hover:text-red-700 font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                모두 지우기
              </button>
              <span className="text-gray-300">|</span>
            </>
          )}
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="닫기"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* 알림 목록 */}
      <div className="overflow-y-auto max-h-[400px]">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7A56E8] mx-auto"></div>
            <p className="mt-2 text-sm">알림을 불러오는 중...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">알림이 없습니다</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <button
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                  !notification.isRead ? "bg-blue-50/50" : ""
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* 아이콘 */}
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* 내용 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p
                          className={`text-sm font-medium ${
                            !notification.isRead
                              ? "text-gray-900"
                              : "text-gray-700"
                          }`}
                        >
                          {notification.title}
                        </p>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          {formatTime(notification.createdAt)}
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* 읽지 않음 표시 */}
                        {!notification.isRead && (
                          <div className="flex-shrink-0 w-2 h-2 bg-[#7A56E8] rounded-full"></div>
                        )}
                        
                        {/* 삭제 버튼 */}
                        <button
                          onClick={(e) => handleDeleteNotification(e, notification.id)}
                          className="flex-shrink-0 p-1 rounded-full hover:bg-red-50 transition-colors group"
                          aria-label="알림 삭제"
                        >
                          <X className="w-3.5 h-3.5 text-gray-400 group-hover:text-red-600 transition-colors" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 하단 링크 */}
      {notifications.length > 0 && (
        <div className="p-3 border-t border-gray-200 bg-gray-50">
          <Link
            href="/profile/notifications"
            onClick={onClose}
            className="block text-center text-sm text-[#7A56E8] font-medium hover:text-[#6D46D1] transition-colors"
          >
            모든 알림 보기
          </Link>
        </div>
      )}
    </div>
  );
}

